import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';

import { Icon, Tooltip } from '~/components';
import type { ArtistDetails, AssetFamily } from '~/modules/artist-roster/types';
import { useAnalytics } from '~/modules/monitoring/analytics/useAnalytics';

interface PreReleaseTableFeedbackCellProps {
  asset: AssetFamily;
  artistData: ArtistDetails;
}

export function PreReleaseTableFeedbackCell({
  asset,
  artistData,
}: PreReleaseTableFeedbackCellProps) {
  const { trackEvent } = useAnalytics('Artist Roster');
  const { t } = useTranslation('roster');
  const [isHovered, setIsHovered] = useState(false);

  const handlePreReleaseSendFeedbackClick = () => {
    trackEvent('artistPreReleasesSendFeedbackButtonClick', {
      artist_id: artistData.partyId,
      artist_name: artistData.name,
      track_family_id: asset.id,
    });
  };

  // Pre-filled feedback form
  const feedbackFormLink = useMemo(() => {
    const { firstWeekStreams = '--', secondWeekStreams = '--' } =
      asset.benchmark || {};

    return `https://docs.google.com/forms/d/e/1FAIpQLSfvZYvC0xKofN9kavgmSLsu3hAX8Rl2XduIE5GUsVSep-QvWg/viewform?usp=pp_url&entry.1328907116=${asset.title}&entry.1887307903=${asset.id}&entry.2062332651=${firstWeekStreams}&entry.1952744564=${secondWeekStreams}`;
  }, [asset]);

  return (
    <Link
      aria-label={t('preReleases.tooltips.benchmarkFeedback')}
      className="rounded-full border p-sm transition-colors border-transparent hover:bg-emphasis-subtle hover:border-emphasis-strong"
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      to={feedbackFormLink}
      target="_blank"
      rel="noopener noreferrer"
      onClick={handlePreReleaseSendFeedbackClick}
    >
      <Tooltip
        content={t('preReleases.tooltips.benchmarkFeedback')}
        contentClassName="rounded-sm border p-md text-body-sm bg-level-03 border-emphasis-subtle text-strong"
        sideOffset={15}
        side="bottom"
        size="sm"
        align="end"
        alignOffset={5}
        triggerAsChild
      >
        <div className="flex size-md items-center justify-center">
          <Icon
            type="comment-lines"
            variant={isHovered ? 'solid' : 'regular'}
            size="sm"
          />
        </div>
      </Tooltip>
    </Link>
  );
}

PreReleaseTableFeedbackCell.displayName = 'PreReleaseTableFeedbackCell';
